import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Monogram, GoldDivider } from "./Ornaments";
import { GoldParticles } from "./GoldParticles";
import { COUPLE } from "../lib/invitationData";

const ease = [0.22, 0.61, 0.36, 1];

export const Hero = () => {
    const scrollNext = () => {
        const el = document.getElementById("invitation");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        // trigger the cinematic autoscroll journey
        window.dispatchEvent(new CustomEvent("autoscroll:start"));
    };

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden paper-grain flex items-center justify-center"
            style={{
                background:
                    "radial-gradient(ellipse at top, #1a1611 0%, #12100b 45%, #0a0805 100%)",
            }}
        >
            <GoldParticles count={28} />

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 2.2, ease }}
                className="relative z-10 max-w-3xl w-full px-8 text-center flex flex-col items-center"
            >
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 1.4, ease }}
                    className="tracking-editorial uppercase text-[10px] text-[color:var(--gold-deep)] mb-8"
                >
                    A Wedding Invitation
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.0, duration: 1.8, ease }}
                    className="gold-shimmer"
                >
                    <Monogram size={170} />
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.4, duration: 1.3, ease }}
                    className="mt-14 tracking-editorial uppercase text-[10px] text-[color:var(--muted)]"
                >
                    Together with their families
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.7, duration: 1.4, ease }}
                    className="font-serif-display mt-6 text-[color:var(--charcoal)] text-4xl sm:text-5xl md:text-6xl leading-[1.05] font-light"
                >
                    {COUPLE.groom}
                    <span className="block my-3 text-[color:var(--gold)] text-3xl md:text-4xl italic font-light">
                        &
                    </span>
                    {COUPLE.bride}
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 3.4, duration: 1.3, ease }}
                    className="mt-8 font-serif-display italic text-lg md:text-xl text-[color:var(--charcoal-soft)]"
                >
                    {COUPLE.inshallah}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 3.7, duration: 1.2, ease }}
                    className="mt-8"
                >
                    <GoldDivider />
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 3.9, duration: 1.2, ease }}
                    className="mt-6 tracking-[0.4em] uppercase text-xs text-[color:var(--charcoal-soft)]"
                    data-testid="hero-date"
                >
                    {COUPLE.heroDate}
                </motion.p>

                <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 4.3, duration: 1.2, ease }}
                    whileHover={{ y: -2 }}
                    onClick={scrollNext}
                    data-testid="begin-journey-button"
                    className="btn-outline-gold mt-12"
                >
                    Begin the Journey
                </motion.button>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 5, duration: 2 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[color:var(--muted)]"
                aria-hidden="true"
            >
                <span className="text-[9px] tracking-[0.4em] uppercase">
                    Scroll
                </span>
                <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{
                        duration: 2.4,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                >
                    <ChevronDown size={14} />
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
